// --------------------------------------------------------------
// Created On: 2025-03-21
// Author: Dimitra Weinstein
//
// Last Modified: 2026-02-11
// Modified By: Hannah Vaughan
//
// Copyright 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment, useState } from "react";
import { ModalBody } from "react-bootstrap";
import useReduxSelector from "src/hooks/useReduxSelector";
import {
  API,
  MAX_REPORT_NAME_LENGTH,
  SCHEDULE_TYPE_MONTHLY,
  VALID_EMAIL_REGULAR_EXPRESSION,
} from "src/constants/miscellaneous";
import {
  CREATE_REPORTS_PERMISSION,
  DELETE_REPORTS_PERMISSION,
  UPDATE_REPORTS_PERMISSION,
} from "src/constants/permissions";
import { REPORT_TYPES } from "src/constants/reducerActions";
import useApi from "src/hooks/useApi";
import userHasPermission from "src/utilities/userHasPermission";
import deepCopy from "src/utilities/deepCopy";
import stringIsValidNumber from "src/utilities/stringIsValidNumber";
import scheduleFromLocalToUtc from "src/utilities/time/scheduleFromLocalToUtc";
import apiRequest from "src/utilities/api/apiRequest";
import getApiError from "src/utilities/api/getApiError";
import scheduleFromUtcToLocal from "src/utilities/time/scheduleFromUtcToLocal";
import Modal from "src/components/Modal/Modal";
import Spinner from "src/components/Spinner/Spinner";
import Error from "src/components/Error/Error";
import IconTooltip from "src/components/IconTooltip/IconTooltip";
import ConfirmModal from "src/components/ConfirmModal/ConfirmModal";
import SaveChangesModal from "src/components/SaveChangesModal/SaveChangesModal";
import ModalHeader from "src/components/ModalHeader/ModalHeader";
import ModalFooter from "src/components/ModalFooter/ModalFooter";
import ReportEmailForm from "../ReportEmailForm/ReportEmailForm";
import ScheduleForm from "../ScheduleForm/ScheduleForm";
import AssetDetailsForm from "./AssetDetailsForm/AssetDetailsForm";
import ProjectDetailsForm from "./ProjectDetailsForm/ProjectDetailsForm";
import styles from "./MemphisReportModal.module.scss";

// Modal for creating, updating, or deleting a Memphis report.
export default function MemphisReportModal(props: Props): Component {
  const initialMemphisReport: MemphisReport = {
    reportMemphisId: 0,
    assetId: 0,
    name: "",
    owner: "",
    address: "",
    sewerBasin: "",
    inspectorName: "",
    capacityOfPrimaryPump: "",
    capacityOfSecondaryPump: "",
    pumpHorsepower: "",
    forceMain: "",
    maximumDischarge: "",
    approvedPumpRuntime: "",
    storageVolumeOfTank: "",
    pumpVoltage: "",
    scheduledDayOfMonth: 0,
    scheduledTimeUtc: "",
    emailAddresses: [],
  };
  const initialMemphisReportScheduleSettings: ReportSchedule[] = [
    { reportScheduleId: 0, type: SCHEDULE_TYPE_MONTHLY, value: "01:09:00" },
  ];
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [showConfirmDelete, setShowConfirmDelete] = useState<boolean>(false);
  const [showConfirmExit, setShowConfirmExit] = useState<boolean>(false);
  const [previousReport, setPreviousReport] = useState<MemphisReport>(initialMemphisReport);
  const [previousMemphisScheduleSettings, setPreviousMemphisScheduleSettings] = useState<ReportSchedule[] | null>(
    initialMemphisReportScheduleSettings
  );
  const [name, setName] = useState<string>("");
  const [enabled, setEnabled] = useState<boolean>(true);
  const [emailAddresses, setEmailAddresses] = useState<FormEmailAddress[]>([]);
  const [memphisScheduleSettings, setMemphisScheduleSettings] = useState<ReportSchedule[] | null>(
    initialMemphisReportScheduleSettings
  );
  const [assetId, setAssetId] = useState<number>(0);
  const [assetName, setAssetName] = useState<string>("");
  const [memphisReportProjectOwner, setMemphisReportProjectOwner] = useState<string>("");
  const [address, setAddress] = useState<string>("");
  const [sewerBasin, setSewerBasin] = useState<string>("");
  const [inspectorName, setInspectorName] = useState<string>("");
  const [capacityOfPrimaryPump, setCapacityOfPrimaryPump] = useState<string>("");
  const [capacityOfSecondaryPump, setCapacityOfSecondaryPump] = useState<string>("");
  const [pumpHorsepower, setPumpHorsepower] = useState<string>("");
  const [forceMain, setForceMain] = useState<string>("");
  const [maximumDischarge, setMaximumDischarge] = useState<string>("");
  const [approvedPumpRuntime, setApprovedPumpRuntime] = useState<string>("");
  const [storageVolumeOfTank, setStorageVolumeOfTank] = useState<string>("");
  const [pumpVoltage, setPumpVoltage] = useState<string>("");
  const [numberOfPumps, setNumberOfPumps] = useState<number>(0);
  const [isTypeDisabled, setIsTypeDisabled] = useState<boolean>(true);
  const currentUser = useReduxSelector((state) => state.currentUser);

  // Get detailed memphis report information from the API.
  useApi(
    () => {
      setLoading(props.reportMemphisId > 0);
      return props.reportMemphisId > 0;
    },
    {
      method: "GET",
      url: `${API}/company/${currentUser.companyId}/report-memphis/${props.reportMemphisId}`,
    },
    async (response: Response, responseBody: MemphisGetResponseBody) => {
      if (response.ok && responseBody) {
        const memphisReport = {
          reportMemphisId: responseBody.reportMemphisId,
          assetId: responseBody.assetId,
          name: responseBody.name,
          owner: responseBody.owner,
          address: responseBody.address,
          sewerBasin: responseBody.sewerBasin,
          inspectorName: responseBody.inspectorName,
          capacityOfPrimaryPump: responseBody.capacityOfPrimaryPump === null ? "" : responseBody.capacityOfPrimaryPump,
          capacityOfSecondaryPump:
            responseBody.capacityOfSecondaryPump === null ? "" : responseBody.capacityOfSecondaryPump,
          pumpHorsepower: responseBody.pumpHorsepower === null ? "" : responseBody.pumpHorsepower,
          forceMain: responseBody.forceMain === null ? "" : responseBody.forceMain,
          maximumDischarge: responseBody.maximumDischarge === null ? "" : responseBody.maximumDischarge,
          approvedPumpRuntime: responseBody.approvedPumpRuntime === null ? "" : responseBody.approvedPumpRuntime,
          storageVolumeOfTank: responseBody.storageVolumeOfTank === null ? "" : responseBody.storageVolumeOfTank,
          pumpVoltage: responseBody.pumpVoltage === null ? "" : responseBody.pumpVoltage,
          scheduledDayOfMonth: responseBody.scheduledDayOfMonth,
          scheduledTimeUtc:
            responseBody.scheduledTimeUtc.split(":")[0] + ":" + responseBody.scheduledTimeUtc.split(":")[1],
          emailAddresses: responseBody.emailAddresses,
        };
        const memphisReportScheduleSettings: ReportSchedule[] = [
          {
            reportScheduleId: 0,
            type: SCHEDULE_TYPE_MONTHLY,
            value: `${padDaysOfMonth(responseBody.scheduledDayOfMonth)}:${
              responseBody.scheduledTimeUtc.split(":")[0]
            }:${responseBody.scheduledTimeUtc.split(":")[1]}`,
          },
        ];
        const scheduleSettingsMemphis =
          scheduleFromUtcToLocal(memphisReportScheduleSettings) === null
            ? initialMemphisReportScheduleSettings
            : scheduleFromUtcToLocal(memphisReportScheduleSettings);
        const memphisReportAssetName = getAssetNameById(responseBody.assetId);
        const formEmailAddresses = responseBody.emailAddresses.map((emailAddress, i) => {
          return { formId: i + 1, value: emailAddress };
        });
        setPreviousReport(memphisReport);
        setPreviousMemphisScheduleSettings(scheduleSettingsMemphis);
        setAssetId(responseBody.assetId);
        setAssetName(memphisReportAssetName);
        setName(responseBody.name);
        setMemphisReportProjectOwner(responseBody.owner);
        setAddress(responseBody.address);
        setSewerBasin(responseBody.sewerBasin);
        setInspectorName(responseBody.inspectorName);
        setCapacityOfPrimaryPump(responseBody.capacityOfPrimaryPump === null ? "" : responseBody.capacityOfPrimaryPump);
        setCapacityOfSecondaryPump(
          responseBody.capacityOfSecondaryPump === null ? "" : responseBody.capacityOfSecondaryPump
        );
        setNumberOfPumps(responseBody.capacityOfSecondaryPump !== null ? 2 : 1);
        setPumpHorsepower(responseBody.pumpHorsepower === null ? "" : responseBody.pumpHorsepower);
        setForceMain(responseBody.forceMain === null ? "" : responseBody.forceMain);
        setMaximumDischarge(responseBody.maximumDischarge === null ? "" : responseBody.maximumDischarge);
        setApprovedPumpRuntime(responseBody.approvedPumpRuntime === null ? "" : responseBody.approvedPumpRuntime);
        setStorageVolumeOfTank(responseBody.storageVolumeOfTank === null ? "" : responseBody.storageVolumeOfTank);
        setPumpVoltage(responseBody.pumpVoltage === null ? "" : responseBody.pumpVoltage);
        setEmailAddresses(formEmailAddresses);
        setMemphisScheduleSettings(scheduleSettingsMemphis);
        setIsTypeDisabled(true);
        setErrorMessage("");
      } else {
        setErrorMessage("Internal server error. Unable to get report settings.");
      }
      setLoading(false);
    },
    [props.reportMemphisId]
  );

  // Function to add a leading zero if the day of the month is less than 10.
  function padDaysOfMonth(dayOfMonth: number): string {
    if (dayOfMonth < 10 && dayOfMonth > 0) {
      return dayOfMonth.toString().padStart(2, "0");
    } else {
      return dayOfMonth.toString();
    }
  }

  // Get the asset name by asset ID.
  function getAssetNameById(assetId: number): string {
    const asset = props.assets.find((asset) => asset.assetId === assetId);
    if (asset !== undefined) {
      return asset.name;
    }
    return "";
  }

  // Get the asset ID by asset name.
  function getAssetIdByName(assetName: string): number {
    const asset = props.assets.find((asset) => asset.name === assetName);
    if (asset !== undefined) {
      return asset.assetId;
    } else {
      return 0;
    }
  }

  // Set the number of pumps.
  function handleNumberOfPumpsChange(numberOfPumps: number): void {
    setNumberOfPumps(numberOfPumps);
    if (numberOfPumps !== 2) {
      setCapacityOfSecondaryPump("");
    }
  }

  // Exit the modal.
  function exitModal(): void {
    // Get comparable current and previous email addresses.
    const emailAddressValues = emailAddresses.map((emailAddress) => emailAddress.value);
    let previousEmailAddressValues: string[] = [];
    if (previousReport.emailAddresses !== undefined) {
      previousEmailAddressValues = previousReport.emailAddresses;
    }
    // Get comparable current and previous number of pumps.
    let previousNumberOfPumps = 0;
    if (numberOfPumps !== undefined) {
      previousNumberOfPumps = numberOfPumps;
    }
    // Get comparable current and previous schedule settings.
    let tempScheduleSettings: ReportSchedule[] | null = [];
    if (memphisScheduleSettings !== null) {
      tempScheduleSettings = memphisScheduleSettings.map((scheduleSetting) => {
        return {
          reportScheduleId: scheduleSetting.reportScheduleId,
          type: scheduleSetting.type,
          value: scheduleSetting.value,
        };
      });
    }
    // Check to see if anything has changed in the report settings.
    if (
      name === previousReport.name &&
      memphisReportProjectOwner === previousReport.owner &&
      address === previousReport.address &&
      sewerBasin === previousReport.sewerBasin &&
      inspectorName === previousReport.inspectorName &&
      pumpHorsepower === previousReport.pumpHorsepower &&
      forceMain === previousReport.forceMain &&
      maximumDischarge === previousReport.maximumDischarge &&
      approvedPumpRuntime === previousReport.approvedPumpRuntime &&
      storageVolumeOfTank === previousReport.storageVolumeOfTank &&
      pumpVoltage === previousReport.pumpVoltage &&
      assetId === previousReport.assetId &&
      capacityOfPrimaryPump === previousReport.capacityOfPrimaryPump &&
      capacityOfSecondaryPump === previousReport.capacityOfSecondaryPump &&
      JSON.stringify(emailAddressValues) === JSON.stringify(previousEmailAddressValues) &&
      JSON.stringify(tempScheduleSettings) === JSON.stringify(previousMemphisScheduleSettings) &&
      numberOfPumps === previousNumberOfPumps
    ) {
      // Since there have been no changes, exit the modal.
      discardChanges();
    } else {
      // There have been changes, so ask the user if they want to save their changes.
      setShowConfirmExit(true);
    }
  }

  // Add an empty email address.
  function addEmailAddress(): void {
    const emailAddressDeepCopy = deepCopy(emailAddresses);
    let formId = 1;
    emailAddressDeepCopy.forEach((emailAddress) => {
      if (emailAddress.formId >= formId) {
        formId = emailAddress.formId + 1;
      }
    });
    const emailAddress = {
      formId: formId,
      value: "",
    };
    emailAddressDeepCopy.push(emailAddress);
    setEmailAddresses(emailAddressDeepCopy);
  }

  // Update an email address.
  function updateEmailAddress(formId: number, value: string): void {
    const emailAddressDeepCopy = deepCopy(emailAddresses);
    const emailAddress = emailAddressDeepCopy.find((emailAddress) => emailAddress.formId === formId);
    if (emailAddress !== undefined) {
      emailAddress.value = value;
      setEmailAddresses(emailAddressDeepCopy);
    }
  }

  // Delete an email address.
  function deleteEmailAddress(formId: number): void {
    const emailAddressesDeepCopy = deepCopy(emailAddresses);
    const index = emailAddressesDeepCopy.findIndex((emailAddress) => emailAddress.formId === formId);
    if (index >= 0) {
      emailAddressesDeepCopy.splice(index, 1);
      setEmailAddresses(emailAddressesDeepCopy);
    }
  }

  // Save changes.
  function saveChanges(): void {
    setShowConfirmExit(false);
    setErrorMessage("");
    if (props.isCreatingNewRecord) {
      void createReport();
    } else {
      void editReport(props.reportMemphisId);
    }
  }

  // Exit without saving changes.
  function discardChanges(): void {
    props.onClose();
    setPreviousMemphisScheduleSettings(initialMemphisReportScheduleSettings);
    setPreviousReport(initialMemphisReport);
    setMemphisScheduleSettings(initialMemphisReportScheduleSettings);
    setName("");
    setEnabled(true);
    setEmailAddresses([]);
    setAssetId(0);
    setAssetName("");
    setMemphisReportProjectOwner("");
    setAddress("");
    setSewerBasin("");
    setInspectorName("");
    setCapacityOfPrimaryPump("");
    setCapacityOfSecondaryPump("");
    setPumpHorsepower("");
    setForceMain("");
    setMaximumDischarge("");
    setApprovedPumpRuntime("");
    setStorageVolumeOfTank("");
    setPumpVoltage("");
    setNumberOfPumps(0);
    setIsTypeDisabled(true);
    setShowConfirmDelete(false);
    setShowConfirmExit(false);
    setErrorMessage("");
  }

  // Validate the report.
  function reportIsValid(): boolean {
    const assetId = getAssetIdByName(assetName);
    const primaryPumpCapacityIsInvalid =
      capacityOfPrimaryPump !== "" &&
      (!stringIsValidNumber(capacityOfPrimaryPump) || parseInt(capacityOfPrimaryPump) <= 0);
    const secondaryPumpCapacityIsInvalid: boolean =
      numberOfPumps === 2 &&
      capacityOfSecondaryPump !== "" &&
      (!stringIsValidNumber(capacityOfSecondaryPump) || parseInt(capacityOfSecondaryPump) <= 0);
    const pumpHorsepowerIsInvalid = !stringIsValidNumber(pumpHorsepower) || parseInt(pumpHorsepower) <= 0;
    const forceMainIsInvalid = !stringIsValidNumber(forceMain) || parseInt(forceMain) <= 0;
    const maximumDischargeIsInvalid = !stringIsValidNumber(maximumDischarge) || parseInt(maximumDischarge) <= 0;
    const storageVolumeOfTankIsInvalid =
      !stringIsValidNumber(storageVolumeOfTank) || parseInt(storageVolumeOfTank) <= 0;
    const pumpVoltageIsInvalid = !stringIsValidNumber(pumpVoltage) || parseInt(pumpVoltage) <= 0;

    let invalidEmailIndex = -1;
    emailAddresses.forEach((emailAddress, i) => {
      if (!VALID_EMAIL_REGULAR_EXPRESSION.test(emailAddress.value.toLowerCase())) {
        invalidEmailIndex = i;
      }
    });

    if (name.trim().length === 0) {
      setErrorMessage("A report is required to have a name.");
      return false;
    } else if (assetName.trim().length === 0) {
      setErrorMessage("A report is required to have an asset name.");
      return false;
    } else if (numberOfPumps === 0 || capacityOfPrimaryPump === "") {
      setErrorMessage("A report is required to have at least one selected pump with a capacity value.");
      return false;
    } else if (numberOfPumps === 2 && capacityOfSecondaryPump === "") {
      setErrorMessage("A report with two pumps is required to have a secondary pump capacity value.");
      return false;
    } else if (primaryPumpCapacityIsInvalid) {
      setErrorMessage("The primary pump capacity value must be a valid number that is greater than 0.");
      return false;
    } else if (secondaryPumpCapacityIsInvalid) {
      setErrorMessage("The secondary pump capacity value must be a valid number that is greater than 0.");
      return false;
    } else if (pumpHorsepowerIsInvalid) {
      setErrorMessage("The pump horsepower value must be a valid number that is greater than 0.");
      return false;
    } else if (forceMainIsInvalid) {
      setErrorMessage("The force main value must be a valid number that is greater than 0.");
      return false;
    } else if (maximumDischargeIsInvalid) {
      setErrorMessage("The maximum discharge value must be a valid number that is greater than 0.");
      return false;
    } else if (storageVolumeOfTankIsInvalid) {
      setErrorMessage("The storage volume of tank value must be a valid number that is greater than 0.");
      return false;
    } else if (pumpVoltageIsInvalid) {
      setErrorMessage("The pump voltage value must be a valid number that is greater than 0.");
      return false;
    } else if (memphisScheduleSettings !== null && memphisScheduleSettings.length === 0) {
      setErrorMessage("The entered schedule is not valid.");
      return false;
    } else if (invalidEmailIndex >= 0) {
      setErrorMessage(`Email address #${invalidEmailIndex + 1} is invalid.`);
      return false;
    } else if (assetId <= 0) {
      setErrorMessage("A valid asset is not selected.");
      return false;
    } else {
      return true;
    }
  }

  // Create a new report.
  async function createReport(): Promise<void> {
    if (reportIsValid()) {
      if (memphisScheduleSettings !== null) {
        const utcScheduleSettings = scheduleFromLocalToUtc(memphisScheduleSettings);

        if (utcScheduleSettings !== null) {
          const emailAddressValues = emailAddresses.map((emailAddress) => emailAddress.value);

          const noDuplicateEmailAddressValues = emailAddressValues.filter(
            (emailAddressValue, index) => emailAddressValues.indexOf(emailAddressValue) === index
          );

          const requestBody = {
            assetId: getAssetIdByName(assetName),
            name: name,
            owner: memphisReportProjectOwner,
            address: address,
            sewerBasin: sewerBasin,
            inspectorName: inspectorName,
            capacityOfPrimaryPump: capacityOfPrimaryPump === "" ? null : capacityOfPrimaryPump,
            capacityOfSecondaryPump: capacityOfSecondaryPump === "" ? null : capacityOfSecondaryPump,
            pumpHorsepower: pumpHorsepower === "" ? null : pumpHorsepower,
            forceMain: forceMain === "" ? null : forceMain,
            maximumDischarge: maximumDischarge === "" ? null : maximumDischarge,
            approvedPumpRuntime: approvedPumpRuntime === "" ? null : approvedPumpRuntime,
            storageVolumeOfTank: storageVolumeOfTank === "" ? null : storageVolumeOfTank,
            pumpVoltage: pumpVoltage === "" ? null : pumpVoltage,
            scheduleDayOfMonth: parseInt(utcScheduleSettings[0].value.split(":")[0]),
            scheduleTimeUtc: `${utcScheduleSettings[0].value.split(":")[1]}:${
              utcScheduleSettings[0].value.split(":")[2]
            }`,
            emailAddresses: noDuplicateEmailAddressValues,
          };

          setLoading(true);
          const [response, responseBody] = (await apiRequest(
            `${API}/company/${currentUser.companyId}/report-memphis`,
            "POST",
            requestBody
          )) as [Response, PostResponseBody];
          setLoading(false);

          if (response.ok) {
            const reportDeepCopy = deepCopy(previousReport);
            reportDeepCopy.reportMemphisId = responseBody.memphisReportId;
            reportDeepCopy.name = name;
            setPreviousReport(reportDeepCopy);
            props.onAction({
              type: REPORT_TYPES.CREATE_REPORT,
              payload: {
                memphisReport: reportDeepCopy,
              },
            });
            setShowConfirmExit(false);
            setErrorMessage("");
            props.onClose();
          } else {
            setErrorMessage(await getApiError(response, "Unable to create report."));
          }
        } else {
          setErrorMessage("Invalid schedule settings.");
        }
      }
    }
  }

  // Edit an existing report.
  async function editReport(reportMemphisId: number): Promise<void> {
    if (reportIsValid()) {
      if (memphisScheduleSettings !== null) {
        // Convert local schedule settings to UTC.
        const utcScheduleSettings = scheduleFromLocalToUtc(memphisScheduleSettings);

        if (utcScheduleSettings !== null) {
          const emailAddressValues = emailAddresses.map((emailAddress) => emailAddress.value);

          const noDuplicateEmailAddressValues = emailAddressValues.filter(
            (emailAddressValue, index) => emailAddressValues.indexOf(emailAddressValue) === index
          );

          const requestBody = {
            assetId: getAssetIdByName(assetName),
            name: name,
            owner: memphisReportProjectOwner,
            address: address,
            sewerBasin: sewerBasin,
            inspectorName: inspectorName,
            capacityOfPrimaryPump: capacityOfPrimaryPump === "" ? null : capacityOfPrimaryPump,
            capacityOfSecondaryPump: capacityOfSecondaryPump === "" ? null : capacityOfSecondaryPump,
            pumpHorsepower: pumpHorsepower === "" ? null : pumpHorsepower,
            forceMain: forceMain === "" ? null : forceMain,
            maximumDischarge: maximumDischarge === "" ? null : maximumDischarge,
            approvedPumpRuntime: approvedPumpRuntime === "" ? null : approvedPumpRuntime,
            storageVolumeOfTank: storageVolumeOfTank === "" ? null : storageVolumeOfTank,
            pumpVoltage: pumpVoltage === "" ? null : pumpVoltage,
            scheduledDayOfMonth: parseInt(utcScheduleSettings[0].value.split(":")[0]),
            scheduledTimeUtc: `${utcScheduleSettings[0].value.split(":")[1]}:${
              utcScheduleSettings[0].value.split(":")[2]
            }`,
            emailAddresses: noDuplicateEmailAddressValues,
          };
          setLoading(true);
          const [response] = (await apiRequest(
            `${API}/company/${currentUser.companyId}/report-memphis/${reportMemphisId}`,
            "PUT",
            requestBody
          )) as [Response, PutResponseBody];
          setLoading(false);

          if (response.ok) {
            const reportDeepCopy = deepCopy(previousReport);
            reportDeepCopy.reportMemphisId = reportMemphisId;
            reportDeepCopy.name = name;
            setPreviousReport(reportDeepCopy);
            props.onAction({
              type: REPORT_TYPES.UPDATE_REPORT,
              payload: {
                memphisReport: reportDeepCopy,
              },
            });
            setErrorMessage("");
            props.onClose();
          } else {
            setErrorMessage(await getApiError(response, "Unable to update report."));
          }
        } else {
          setErrorMessage("Invalid schedule settings.");
        }
      }
    }
  }

  // Delete a report.
  async function deleteReport(reportMemphisId: number): Promise<void> {
    setLoading(true);
    const [response] = (await apiRequest(
      `${API}/company/${currentUser.companyId}/report-memphis/${reportMemphisId}`,
      "DELETE",
      null
    )) as [Response, DeleteResponseBody];
    setLoading(false);

    if (response.ok) {
      discardChanges();
      props.onAction({
        type: REPORT_TYPES.DELETE_REPORT,
        payload: {
          reportMemphisId: reportMemphisId,
        },
      });
    } else {
      setShowConfirmDelete(false);
      setErrorMessage(await getApiError(response, "Unable to delete report."));
    }
  }

  // Returns whether the current user is allowed to edit the form with their current permissions.
  function formIsEditable(): boolean {
    return (
      (props.isCreatingNewRecord && userHasPermission([[CREATE_REPORTS_PERMISSION]])) ||
      (!props.isCreatingNewRecord && userHasPermission([[UPDATE_REPORTS_PERMISSION]]))
    );
  }

  return (
    <div>
      <Spinner loading={loading} />

      <Modal
        show={true}
        onHide={() => exitModal()}
        backdropClassName={`${styles.modal} ${styles.backdrop}`}
        style={{ zIndex: "var(--modal-z-index)" }}
        size="xl"
        centered
        animation
      >
        <ModalHeader>
          <h5 className="font-weight-bold">{props.isCreatingNewRecord ? "Create Report" : "Edit Report"}</h5>
        </ModalHeader>

        <ModalBody>
          <div className="mx-2 mt-3">
            <label className="mb-3">Name</label>
            <input
              data-test="report-modal-name-input"
              className="form-control mx-auto mb-4"
              type="text"
              maxLength={MAX_REPORT_NAME_LENGTH}
              value={name}
              disabled={!formIsEditable()}
              onChange={(e) => setName(e.target.value)}
            />

            {/* Toggle email sending on or off. */}
            <div className={`${styles.switch} my-4`}>
              <div className="form-check form-switch">
                <input
                  className="form-check-input me-3"
                  type="checkbox"
                  checked={enabled}
                  disabled={!formIsEditable()}
                  onChange={() => setEnabled(!enabled)}
                />
                {enabled ? (
                  <label onClick={() => setEnabled(!enabled)}>
                    This report will be emailed at the scheduled times.
                  </label>
                ) : (
                  <label onClick={() => setEnabled(!enabled)}>Sending emails is disabled for this report.</label>
                )}
              </div>
            </div>

            {/* Email addresses. */}
            <div className={styles.border}>
              <div className="mx-1">
                <label className={styles.title}>
                  <span>Email Addresses&nbsp;</span>
                  <IconTooltip
                    id="email-address-tooltip"
                    icon="info-circle"
                    message="This is a list of the email addresses that will receive this report."
                    color="var(--info-tooltip)"
                  />
                </label>

                <button
                  data-test="report-modal-add-email-button"
                  type="submit"
                  className={`${styles.addButton} btn btn-success float-end`}
                  disabled={!formIsEditable()}
                  onClick={() => addEmailAddress()}
                >
                  <span className="d-none d-sm-inline">Add Email Address</span>
                  <i className="d-inline d-sm-none fa fa-fw fa-plus fa-xs" />
                </button>
              </div>

              {emailAddresses.map((emailAddress, i) => (
                <ReportEmailForm
                  key={emailAddress.formId}
                  formIndex={i}
                  formId={emailAddress.formId}
                  value={emailAddress.value}
                  disabled={!formIsEditable()}
                  onChange={(formId, value) => {
                    updateEmailAddress(formId, value);
                  }}
                  onDelete={(formId) => deleteEmailAddress(formId)}
                />
              ))}
            </div>

            {/* Project Details */}
            <div className={styles.border}>
              <div className="mx-1">
                <label className={styles.title}>
                  <span>Project Details&nbsp;</span>
                  <IconTooltip
                    id="project-details-tooltip"
                    icon="info-circle"
                    message="General project information that should appear on each report that is generated."
                    color="var(--info-tooltip)"
                  />
                </label>

                <ProjectDetailsForm
                  disabled={!formIsEditable()}
                  reportMemphisId={props.reportMemphisId}
                  assetId={assetId}
                  name={name}
                  owner={memphisReportProjectOwner}
                  address={address}
                  sewerBasin={sewerBasin}
                  inspectorName={inspectorName}
                  capacityOfPrimaryPump={capacityOfPrimaryPump}
                  capacityOfSecondaryPump={capacityOfSecondaryPump}
                  pumpHorsepower={pumpHorsepower}
                  forceMain={forceMain}
                  maximumDischarge={maximumDischarge}
                  approvedPumpRuntime={approvedPumpRuntime}
                  storageVolumeOfTank={storageVolumeOfTank}
                  pumpVoltage={pumpVoltage}
                  emailAddresses={emailAddresses}
                  onOwnerChange={(owner) => setMemphisReportProjectOwner(owner)}
                  onAddressChange={(address) => setAddress(address)}
                  onSewerBasinChange={(sewerBasin) => setSewerBasin(sewerBasin)}
                  onInspectorNameChange={(inspectorName) => setInspectorName(inspectorName)}
                  onCapacityOfPrimaryPumpChange={(capacityOfPrimaryPump) =>
                    setCapacityOfPrimaryPump(capacityOfPrimaryPump)
                  }
                  onCapacityOfSecondaryPumpChange={(capacityOfSecondaryPump) =>
                    setCapacityOfSecondaryPump(capacityOfSecondaryPump)
                  }
                  onPumpHorsepowerChange={(pumpHorsepower) => setPumpHorsepower(pumpHorsepower)}
                  onForceMainChange={(forceMain) => setForceMain(forceMain)}
                  onMaximumDischargeChange={(maximumDischarge) => setMaximumDischarge(maximumDischarge)}
                  onApprovedPumpRuntimeChange={(approvedPumpRuntime) => setApprovedPumpRuntime(approvedPumpRuntime)}
                  onStorageVolumeOfTankChange={(storageVolumeOfTank) => setStorageVolumeOfTank(storageVolumeOfTank)}
                  onPumpVoltageChange={(pumpVoltage) => setPumpVoltage(pumpVoltage)}
                />
              </div>
            </div>

            {/* Asset Details */}
            <div className={styles.border}>
              <div className="mx-1">
                <label className={styles.title}>
                  <span>Asset Details&nbsp;</span>
                  <IconTooltip
                    id="asset-details-tooltip"
                    icon="info-circle"
                    message="Select the asset to collect data from as well as specify additional information that will be used to calculate results on each report that is generated."
                    color="var(--info-tooltip)"
                  />
                </label>

                <AssetDetailsForm
                  disabled={!formIsEditable()}
                  assetName={assetName}
                  numberOfPumps={numberOfPumps}
                  capacityOfPrimaryPump={capacityOfPrimaryPump}
                  capacityOfSecondaryPump={capacityOfSecondaryPump}
                  onAssetNameChange={(assetName) => setAssetName(assetName)}
                  onNumberOfPumpsChange={(numberOfPumps) => handleNumberOfPumpsChange(numberOfPumps)}
                  onCapacityOfPrimaryPumpChange={(capacityOfPrimaryPump) =>
                    setCapacityOfPrimaryPump(capacityOfPrimaryPump)
                  }
                  onCapacityOfSecondaryPumpChange={(capacityOfSecondaryPump) =>
                    setCapacityOfSecondaryPump(capacityOfSecondaryPump)
                  }
                />
              </div>
            </div>

            {/* Schedule Settings */}
            <ScheduleForm
              isMemphisReport={props.memphisReportIsAllowed}
              scheduleSettings={memphisScheduleSettings !== null ? memphisScheduleSettings : []}
              isTypeDisabled={isTypeDisabled}
              disabled={!formIsEditable()}
              onChange={(scheduleSettings) => setMemphisScheduleSettings(scheduleSettings)}
            />

            {errorMessage.length > 0 && (
              <div className="mt-4">
                <Error message={errorMessage} />
              </div>
            )}
          </div>
        </ModalBody>
        <ModalFooter className={styles.footer}>
          {props.isCreatingNewRecord ? (
            <Fragment>
              <button
                data-test="report-modal-create-report-button"
                className={`${styles.btn} btn btn-primary`}
                type="button"
                onClick={() => createReport()}
              >
                Create Report
              </button>

              <button
                data-test="report-modal-cancel-button"
                className={`${styles.btn} btn btn-secondary`}
                type="button"
                onClick={() => exitModal()}
              >
                Cancel
              </button>
            </Fragment>
          ) : (
            <Fragment>
              {userHasPermission([[DELETE_REPORTS_PERMISSION]]) && (
                <button
                  data-test="report-modal-delete-button"
                  className={`${styles.btn} btn btn-danger me-auto`}
                  type="button"
                  onClick={() => setShowConfirmDelete(true)}
                >
                  Delete Report
                </button>
              )}

              {userHasPermission([[UPDATE_REPORTS_PERMISSION]]) && (
                <button
                  data-test="report-modal-save-changes-button"
                  className={`${styles.btn} btn btn-primary`}
                  type="button"
                  onClick={() => editReport(props.reportMemphisId)}
                >
                  Save Changes
                </button>
              )}

              <button
                data-test="report-modal-close-button"
                className={`${styles.btn} btn btn-secondary`}
                type="button"
                onClick={() => exitModal()}
              >
                Close
              </button>
            </Fragment>
          )}
        </ModalFooter>
      </Modal>

      <ConfirmModal
        showModal={showConfirmDelete}
        title={`Delete '${name}'`}
        content={`Are you sure that you want to delete the report '${name}'?`}
        yesText="Delete Report"
        noText="Cancel"
        danger={true}
        onClose={() => setShowConfirmDelete(false)}
        onYes={() => deleteReport(props.reportMemphisId)}
        onNo={() => setShowConfirmDelete(false)}
      />

      <SaveChangesModal
        showModal={showConfirmExit}
        title="Changes have not been saved!"
        content="Are you sure that you want to exit without saving your changes?"
        onClose={() => setShowConfirmExit(false)}
        onSave={() => saveChanges()}
        onNoSave={() => discardChanges()}
      />
    </div>
  );
}

interface Props {
  reportMemphisId: number;
  isCreatingNewRecord: boolean;
  memphisReportIsAllowed: boolean;
  assets: Asset[];
  onClose: () => void;
  onAction: (action: Action) => void;
}

interface MemphisGetResponseBody {
  reportMemphisId: number;
  assetId: number;
  name: string;
  owner: string;
  address: string;
  sewerBasin: string;
  inspectorName: string;
  capacityOfPrimaryPump: string;
  capacityOfSecondaryPump: string;
  pumpHorsepower: string;
  forceMain: string;
  maximumDischarge: string;
  approvedPumpRuntime: string;
  storageVolumeOfTank: string;
  pumpVoltage: string;
  scheduledDayOfMonth: number;
  scheduledTimeUtc: string;
  emailAddresses: string[];
}

interface PostResponseBody {
  memphisReportId: number;
}

interface PutResponseBody {
  message: string;
}

interface DeleteResponseBody {
  message: string;
}

interface Action {
  type: string;
  payload: Payload;
}

interface Payload {
  reports?: AssetReport[];
  memphisReports?: MemphisReport[];
  report?: AssetReport;
  memphisReport?: MemphisReport;
  reportId?: number;
  reportMemphisId?: number;
}

interface MemphisReport {
  reportMemphisId: number;
  assetId: number;
  name: string;
  owner: string;
  address: string;
  sewerBasin: string;
  inspectorName: string;
  capacityOfPrimaryPump: string;
  capacityOfSecondaryPump: string;
  pumpHorsepower: string;
  forceMain: string;
  maximumDischarge: string;
  approvedPumpRuntime: string;
  storageVolumeOfTank: string;
  pumpVoltage: string;
  scheduledDayOfMonth: number;
  scheduledTimeUtc: string;
  emailAddresses: string[];
}

interface AssetReport {
  reportId: number;
  name: string;
  enabled: boolean;
  type: string;
  hours: number;
  attributes: Attribute[];
  emailAddresses: string[];
  scheduleSettings: ReportSchedule[];
  assetgroups: ReportAssetgroup[];
  isAssetRunning: boolean;
}

interface Attribute {
  attributeId: number;
  name: string;
  isBoolean: boolean;
  unitShortName?: string;
  regEnumId?: number;
}

interface ReportAssetgroup {
  assetgroupId: number;
  name: string;
}

interface Asset {
  assetId: number;
  name: string;
}

interface FormEmailAddress {
  formId: number;
  value: string;
}

interface ReportSchedule {
  reportScheduleId: number;
  type: string;
  value: string;
}
